import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ScatterItemIdentifier } from '@mui/x-charts/models';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { type ReactNode, useState } from 'react';
import { formatCompactCurrency } from '../../lib/format.js';
import { CreativeScoreComponentRow } from '../creatives/CreativeScoreComponentRow.js';
import { scoreTier } from '../creatives/data/creativeScore.js';
import { creativesToCsv, downloadCsv } from '../creatives/data/csv.js';
import { ScoreGauge } from '../creatives/detail/shared/ScoreGauge.js';
import { ExportCsvButton } from '../creatives/ExportCsvButton.js';
import { CreativeRows, METRIC_LABEL_SX } from './CreativeRows.js';
import { columnsOf, currencyOf, type ResultRow, SPEND_METRIC, summaryOf } from './resultRows.js';
import {
  accountScoreOf,
  componentsOf,
  leadingKpiLabelOf,
  SCORE_METRIC,
  type ScorePoint,
  scoreBuckets,
  scoreOf,
  scorePointsOf,
  scoresCsvData,
  viewModeOf,
} from './scoreRows.js';
import type { ResultSelection } from './workflows.types.js';

const STRIP_HEIGHT = 64;
const MAP_HEIGHT = 280;
const ROW_GAUGE_SIZE = 64;
// A small IconButton's footprint; rows without a breakdown keep the space so gauges align.
const TOGGLE_SIZE = 30;

interface CreativeScoresViewProps {
  runId: string;
  result: unknown;
  rows: ResultRow[];
  selection?: ResultSelection;
  onOpen?: (row: ResultRow) => void;
}

function VsAccountChip({
  score,
  accountScore,
}: {
  score: number;
  accountScore: number;
}): ReactNode {
  const delta = Math.round(score - accountScore);
  const ahead = delta >= 0;
  return (
    <Box
      data-testid="vs-account"
      sx={(theme) => ({
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        bgcolor: alpha(ahead ? theme.palette.success.dark : theme.palette.error.dark, 0.1),
        color: ahead ? theme.palette.success.dark : theme.palette.error.dark,
      })}
    >
      {`${ahead ? '↑' : '↓'} ${Math.abs(delta)}`}
    </Box>
  );
}

// The toggle sits inside the clickable row, so its click must not also open the drawer.
function RowScore({
  row,
  accountScore,
  expanded,
  onToggle,
}: {
  row: ResultRow;
  accountScore: number | null;
  expanded: boolean;
  onToggle: () => void;
}): ReactNode {
  const score = scoreOf(row);
  return (
    <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
      {accountScore === null || score === null ? null : (
        <VsAccountChip score={score} accountScore={accountScore} />
      )}
      <Stack sx={{ gap: 0.5, alignItems: 'center' }} data-testid={`score-gauge-${row.id}`}>
        <Typography sx={METRIC_LABEL_SX}>{SCORE_METRIC.toUpperCase()}</Typography>
        {/* The gauge's track ring is a progressbar at 100 even with no score, so it stays hidden. */}
        <Box
          role="img"
          aria-label={
            score === null
              ? 'Creative score unavailable'
              : `Creative score ${Math.round(score)} out of 100`
          }
        >
          <Box aria-hidden="true">
            <ScoreGauge score={score} previous={null} size={ROW_GAUGE_SIZE} />
          </Box>
        </Box>
      </Stack>
      {componentsOf(row).length === 0 ? (
        <Box sx={{ width: TOGGLE_SIZE }} />
      ) : (
        <IconButton
          size="small"
          aria-expanded={expanded}
          aria-label={`Score breakdown for ${row.label}`}
          data-testid={`score-breakdown-toggle-${row.id}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
          sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
        >
          <ExpandMoreOutlined fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}

function ScoreBreakdown({ row }: { row: ResultRow }): ReactNode {
  const components = componentsOf(row);
  if (components.length === 0) return null;
  return (
    <Box
      data-testid={`score-breakdown-${row.id}`}
      sx={{
        px: { xs: 2, lg: 3 },
        pb: 3,
        display: 'grid',
        gap: 3,
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      }}
    >
      {components.map((entry) => (
        <CreativeScoreComponentRow key={entry.name} {...entry} />
      ))}
    </Box>
  );
}

function DistributionStrip({ points }: { points: ScorePoint[] }): ReactNode {
  const theme = useTheme();
  const buckets = scoreBuckets(points);
  const max = Math.max(1, ...buckets.map((bucket) => bucket.count));
  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-end' }} data-testid="score-distribution">
      {buckets.map((bucket) => (
        <Stack key={bucket.from} sx={{ flex: 1, gap: 0.5, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{bucket.count}</Typography>
          <Box
            sx={{
              width: '100%',
              height: (bucket.count / max) * STRIP_HEIGHT,
              minHeight: 2,
              borderRadius: 0.5,
              bgcolor: theme.palette.score[scoreTier(bucket.to)].main,
            }}
          />
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
            {`${bucket.from}–${bucket.to}`}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

// Points carry no display, so only a point that is also a listed row opens the drawer.
function ScoreMap({
  points,
  rows,
  onOpen,
}: {
  points: ScorePoint[];
  rows: ResultRow[];
  onOpen?: (row: ResultRow) => void;
}): ReactNode {
  const rowsById = new Map(rows.map((row) => [row.creativeId, row]));
  const currency = rows[0] === undefined ? '$' : currencyOf(rows[0].values);
  return (
    <Box data-testid="score-map">
      <ScatterChart
        height={MAP_HEIGHT}
        series={[
          {
            data: points.map((point) => ({ id: point.creativeId, x: point.spend, y: point.score })),
            valueFormatter: (_value, { dataIndex }) => {
              const point = points[dataIndex];
              return point === undefined
                ? ''
                : `${rowsById.get(point.creativeId)?.label ?? point.creativeId} · ${Math.round(point.score)} · ${formatCompactCurrency(point.spend, currency)}`;
            },
          },
        ]}
        xAxis={[
          {
            label: SPEND_METRIC,
            valueFormatter: (value: number) => formatCompactCurrency(value, currency),
          },
        ]}
        yAxis={[{ label: SCORE_METRIC, min: 0, max: 100 }]}
        onItemClick={(_event: unknown, item: ScatterItemIdentifier) => {
          const row = rowsById.get(points[item.dataIndex]?.creativeId ?? '');
          if (row !== undefined) onOpen?.(row);
        }}
      />
    </Box>
  );
}

export function CreativeScoresView({
  runId,
  result,
  rows,
  selection,
  onOpen,
}: CreativeScoresViewProps): ReactNode {
  const theme = useTheme();
  const viewMode = viewModeOf(result);
  const accountScore = viewMode === 'benchmark' ? accountScoreOf(result) : null;
  const summary = summaryOf(result);
  const points = scorePointsOf(result);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  const handleToggle = (id: string): void =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <Stack sx={{ gap: 3 }}>
      <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
        {accountScore === null ? null : (
          <Typography
            data-testid="account-score"
            sx={{
              fontSize: 32,
              fontWeight: 800,
              color: theme.palette.score[scoreTier(accountScore)].main,
            }}
          >
            {Math.round(accountScore)}
          </Typography>
        )}
        <Typography sx={{ flex: 1, fontSize: 13, color: 'text.secondary' }}>{summary}</Typography>
        <ExportCsvButton
          onClick={() =>
            downloadCsv(
              `creative-scores-${runId}.csv`,
              creativesToCsv(scoresCsvData(rows), leadingKpiLabelOf(rows)),
            )
          }
        />
      </Stack>
      {viewMode === 'distribution' ? <DistributionStrip points={points} /> : null}
      {viewMode === 'score_map' ? (
        <ScoreMap points={points} rows={rows} {...(onOpen === undefined ? {} : { onOpen })} />
      ) : null}
      <CreativeRows
        rows={rows}
        columns={columnsOf(rows).filter((column) => column.name !== SCORE_METRIC)}
        renderAside={(row) => (
          <RowScore
            row={row}
            accountScore={accountScore}
            expanded={expandedIds.has(row.id)}
            onToggle={() => handleToggle(row.id)}
          />
        )}
        renderDetail={(row) => (expandedIds.has(row.id) ? <ScoreBreakdown row={row} /> : null)}
        {...(selection === undefined ? {} : { selection })}
        {...(onOpen === undefined ? {} : { onOpen })}
      />
    </Stack>
  );
}
